import log from 'loglevel';

import { Command } from './command.interface';
import { TriggerCommand } from '../commands/trigger-command';
import { CallbackCommand } from '../commands/callback-command';
import { ConfigCommand } from '../commands/config-command';
import { SessionStorageManager } from '../browser-utilities/session-storage-manager';

// TODO:
// Implement functionality to s queue over page changes (session cookie??)
//

/**
 * The command queue that will collect and process all commands, callbacks and configurations.
 */
export class CommandQueue {
  static queue: Array<Command> = [];
  private static currentlyExecuted: Command | undefined;

  /**
   * Pushes any commands, callbacks or configuration into the command queue.
   * This method will be assigned to and called as the ce() function.
   */
  static add(...args: any[]): void {
    if (args.length === 0) {
      log.warn(`${window.CrossEngageObject}() was called without arguments.`);
      return;
    }

    switch (typeof args[0]) {
      case 'function':
        this.pushCallback(args[0]);
        break;
      case 'string':
        this.pushTrigger(args.splice(0, 1)[0], args);
        break;
      default:
        if (Object.keys(args[0]).length > 0) {
          this.pushConfig(args[0]);
        } else {
          log.warn(`${typeof args[0]} is not an accepted type for the first argument of the global function object.`);
          return;
        }
    }
    log.debug('Pushed to command queue: ', this.queue[this.queue.length-1]);
    if (this.queue.length === 1 && !this.currentlyExecuted) {
      this.executeNext();
    }
  }

  /**
   * Removes the first element from the queue and executes it.
   */
  static executeNext(): void {
    if(this.queue.length > 0) {
      this.currentlyExecuted = this.queue.shift();
      log.debug('Execute element in command queue: ', this.currentlyExecuted);
      this.currentlyExecuted && this.currentlyExecuted.execute().then(() => {
        this.currentlyExecuted = undefined;
        this.executeNext();
      });
    }
  }

  private static pushTrigger(name: string, args: any[]): void {
    this.queue.push(new TriggerCommand(name, args));
  }

  private static pushCallback(callbackFunction: () => void): void {
    this.queue.push(new CallbackCommand(callbackFunction));
  }

  private static pushConfig(config: {[key: string]: any}): void {
    this.queue.push(new ConfigCommand(config));
  }

  /**
   * Stores Config Commands and a selected list of Trigger Commands in session storage to be executed after
   * a page change on the same host.
   * @param triggerList the list of triggers that can be stored
   */
  static storeSessionQueue(triggerList: string[]): void {
    const argsMap = this.queue
    .filter(command => {
      switch(true) {
        case command instanceof ConfigCommand: 
          return true;
          break;
        case command instanceof TriggerCommand: 
          return triggerList.includes(command.getCtorArgs()[0]);
          break;
        default:
          return false;
      }
    })
    .map(command => command.getCtorArgs());
    SessionStorageManager.setItem('ce.q', JSON.stringify(argsMap));
  }

  /**
   * retrieves an array of command arguments from session storage and returns them.
   * each entry needs to be an array to be used as arguments to create commands.
   */
  static retrieveSessionQueue(): unknown[][] {
    const storedQueueString = SessionStorageManager.getItem('ce.q');
    SessionStorageManager.removeItem('ce.q');
    let storedQueue = [];
    if (storedQueueString) {
      storedQueue = JSON.parse(storedQueueString).map(
        (element: any) => Array.isArray(element) ? element : [element]
      );
    }
    log.debug(`Retrieved ${storedQueue.length} commands from previous page queue.`)
    return storedQueue;
  }
}
